import {useNavigate, useParams} from 'react-router-dom';

import {RoutePath} from '~/constants/routes';
import {RouteSection, getRoute} from '~/utils/route';
import {
  Box,
  Button,
  Typography,
  Divider,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';

import Loading from '~/components/Loading';
import FrontdeskButton from '~/components/FrontdeskButton';
import CheckBox from '~/components/CheckBox';
import PlusSquareIcon from '~/assets/svgs/plus-square.svg?react';
import {ArrowBack} from '@mui/icons-material';
import {
  useClaimDetails,
  useUpdateInsuranceClaim,
  useDeleteBillingItem,
} from '../hooks';
import CustomTable, {ICustomTableColumn} from '~/components/CustomTable';
import {
  InsuranceClaim,
  BillingItem,
} from '$api/finance/insuranceClaims/insuranceClaimDetails';
import TrashIcon from '~/assets/svgs/trash.svg?react';
import DeleteConfirmDialog from '~/components/DeleteConfirmDialog';
import DialogComponent from '~/components/DialogComponent';
import {
  summaryContainerStyles,
  summaryBoxStyles,
  summaryLabelStyles,
  summaryValueStyles,
  verticalDividerStyles,
  containerStyles,
  contentWrapperStyles,
  backButtonStyles,
  headerTitleStyles,
  amountLabelStyles,
  amountValueStyles,
  billingTitleStyles,
  dialogBodyStyles,
  outstandingBalanceTitleStyles,
  outstandingAmountLabelStyles,
  outstandingAmountValueStyles,
  amountStatusContainerStyles,
  amountStatusBoxStyles,
  amountStatusBoxWithLeftMarginStyles,
  dialogButtonContainerStyles,
  headerContainerStyles,
  bottomActionsContainerStyles,
  paidAmountInputStyles,
  notificationInputStyles,
  notificationSectionStyles,
  emailNotificationContainerStyles,
  smsNotificationContainerStyles,
  emailCheckboxStyles,
  smsCheckboxStyles,
  paidAmountLabelStyles,
  amountDisplayContainerStyles,
  editIconContainerInlineStyles,
  outstandingContainer,
  paidAmountContainer,
  paymentLinkContainerStyles,
  paymentLinkLabelStyles,
  paymentLinkStyles,
} from './style';
import EditIcon from '~/assets/svgs/edit-icon-button.svg?react';
import SearchPaymentItem from '~/modules/frontdesk/common/components/AddPaymentItem';
import React, {useState} from 'react';
import useAuthUser from '~/hooks/useAuthUser';
import {UserRoleName} from '~/modules/admin/constant';
import EditClaimDialog from './EditClaimDialog';
import '../styles.scss';
import StatusDropdown from '~/modules/frontdesk/Finance/InsuranceClaims/ClaimDetails/StatusDropdown';
import {
  STATUS_OPTIONS,
  CLAIM_STATUSES,
  BILLING_TABLE_COLUMNS,
  SUMMARY_LABELS,
  BUTTON_LABELS,
  DEFAULT_VALUES,
  VALIDATION_MESSAGES,
  TOOLTIP_MESSAGES,
} from '../constants';
import GenerateInvoiceButton from '~/modules/frontdesk/common/components/GenerateInvoiceButton';
import {formatDateString} from '~/utils/helper';

interface OutstandingBalancePayload {
  insurance_claim: {
    insurance_id: number;
    membership_no: string;
    pre_authorisation_code: string;
    status: string;
    approved_amount: number;
    outstanding_amount: number;
    send_email?: boolean;
    send_sms?: boolean;
  };
}

const ClaimSummary: React.FC<{claim: InsuranceClaim}> = ({claim}) => {
  const {roleName} = useAuthUser()!;
  const isFinanceRole = roleName === UserRoleName.Finance;
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const handleEditClick = () => {
    setIsEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false);
  };

  return (
    <>
      <Box sx={summaryContainerStyles}>
        <Box sx={summaryBoxStyles}>
          <Typography sx={summaryLabelStyles}>
            {SUMMARY_LABELS.LAST_UPDATE}
          </Typography>
          <Typography sx={summaryValueStyles}>
            {claim.last_updated
              ? formatDateString(claim.last_updated)
              : DEFAULT_VALUES.EMPTY_FIELD}
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={verticalDividerStyles} />
        <Box sx={summaryBoxStyles}>
          <Typography sx={summaryLabelStyles}>
            {SUMMARY_LABELS.PATIENT_NAME}
          </Typography>
          <Typography sx={summaryValueStyles}>
            {claim.patient_name || DEFAULT_VALUES.EMPTY_FIELD}
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={verticalDividerStyles} />
        <Box sx={summaryBoxStyles}>
          <Typography sx={summaryLabelStyles}>
            {SUMMARY_LABELS.INSURANCE_PROVIDER}
          </Typography>
          <Typography sx={summaryValueStyles}>
            {claim.insurer_name || DEFAULT_VALUES.EMPTY_FIELD}
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={verticalDividerStyles} />
        <Box sx={summaryBoxStyles}>
          <Typography sx={summaryLabelStyles}>
            {SUMMARY_LABELS.MEMBERSHIP_NUMBER}
          </Typography>
          <Typography sx={summaryValueStyles}>
            {claim.membership_no || DEFAULT_VALUES.EMPTY_FIELD}
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={verticalDividerStyles} />
        <Box sx={summaryBoxStyles}>
          <Typography sx={summaryLabelStyles}>
            {SUMMARY_LABELS.PRE_AUTHORISATION_CODE}
          </Typography>
          <Typography sx={summaryValueStyles}>
            {claim.pre_authorisation_code || DEFAULT_VALUES.EMPTY_FIELD}
          </Typography>
        </Box>
        {isFinanceRole && (
          <Box sx={editIconContainerInlineStyles}>
            <EditIcon
              width={40}
              height={40}
              style={{cursor: 'pointer'}}
              onClick={handleEditClick}
            />
          </Box>
        )}
      </Box>

      <EditClaimDialog
        claim={claim}
        open={isEditDialogOpen}
        onClose={handleEditDialogClose}
      />
    </>
  );
};

const AmountStatusSection: React.FC<{
  claim: InsuranceClaim;
  refetch?: () => void;
}> = ({claim, refetch}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const {sendUpdateInsuranceClaimRequest} = useUpdateInsuranceClaim();
  const [isOutstandingDialogOpen, setOutstandingDialogOpen] = useState(false);
  const [paidAmount, setPaidAmount] = useState<string>('');
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifySMS, setNotifySMS] = useState(false);
  const emailAddress = claim.email || '';
  const phoneNumber = claim.phone || '';
  const [paidAmountError, setPaidAmountError] = useState('');
  const countryCode = claim.country_code || '+44';

  const fullPhoneNumber = countryCode + phoneNumber;

  const availableStatusOptions = STATUS_OPTIONS.filter(
    option => option.value !== CLAIM_STATUSES.NEW_CLAIM
  );

  const isOutstandingStatus = claim.status === CLAIM_STATUSES.OUTSTANDING;

  const calculateTotalAmount = () => {
    if (!claim.items || claim.items.length === 0) {
      return 0;
    }
    return claim.items.reduce((total, item) => {
      if (item.id !== 0 && item.amount) {
        return total + item.amount;
      }
      return total;
    }, 0);
  };

  const totalAmount = calculateTotalAmount();
  const paidAmountNumber = Number(paidAmount) || 0;
  const outstandingAmount = totalAmount - paidAmountNumber;

  const isOutstanding = claim.status === CLAIM_STATUSES.OUTSTANDING;

  const handlePaidAmountChange = (value: string, maxAmount: number) => {
    const cleanValue = value.replace(/[^0-9.]/g, '');
    let num = Number(cleanValue);
    if (num > maxAmount) num = maxAmount;
    const result = num ? num.toString() : '';
    setPaidAmount(result);
    setPaidAmountError('');
    return result;
  };

  const validatePaidAmount = () => {
    if (!paidAmount || paidAmount === '0' || paidAmount === '0.00') {
      setPaidAmountError('Paid amount is required');
      return false;
    }
    if (paidAmountNumber > totalAmount) {
      setPaidAmountError('Paid amount cannot exceed total amount');
      return false;
    }
    setPaidAmountError('');
    return true;
  };

  const handleStatusChange = (status: string) => {
    if (!hasValidBillingItems) {
      return;
    }

    if (isOutstandingStatus) {
      return;
    }

    if (status === CLAIM_STATUSES.NEW_CLAIM) {
      return;
    }

    if (status === CLAIM_STATUSES.OUTSTANDING) {
      setOutstandingDialogOpen(true);
    } else {
      setLoading(true);
      sendUpdateInsuranceClaimRequest(claim.id.toString(), {
        insurance_claim: {
          insurance_id: claim.insurance_id,
          membership_no: claim.membership_no,
          pre_authorisation_code: claim.pre_authorisation_code,
          status,
        },
      });
      setLoading(false);
      if (refetch) {
        refetch();
      }
    }
  };

  const handleOutstandingSave = () => {
    if (!validatePaidAmount()) {
      return;
    }

    const payload: OutstandingBalancePayload = {
      insurance_claim: {
        insurance_id: claim.insurance_id,
        membership_no: claim.membership_no,
        pre_authorisation_code: claim.pre_authorisation_code,
        status: CLAIM_STATUSES.OUTSTANDING,
        approved_amount: paidAmountNumber,
        outstanding_amount: outstandingAmount,
      },
    };

    if (notifyEmail) {
      payload.insurance_claim.send_email = true;
    }
    if (notifySMS) {
      payload.insurance_claim.send_sms = true;
    }

    sendUpdateInsuranceClaimRequest(claim.id.toString(), payload);

    setOutstandingDialogOpen(false);

    setNotifyEmail(false);
    setNotifySMS(false);
    setPaidAmountError('');
    if (refetch) refetch();
  };

  const handleDialogClose = () => {
    setOutstandingDialogOpen(false);
    setNotifyEmail(false);
    setNotifySMS(false);
    setPaidAmountError('');
  };

  const handlePaymentLinkClick = () => {
    if (claim.payment_id) {
      navigate(
        getRoute(
          RouteSection.Finance,
          `${RoutePath.staff.finance.payments}/${claim.payment_id}`
        )
      );
    }
  };

  const dialogBody = (
    <Box sx={dialogBodyStyles}>
      <Typography variant="h6" sx={outstandingBalanceTitleStyles}>
        Outstanding balance
      </Typography>
      <Box sx={paidAmountContainer}>
        <Typography variant="subtitle1" sx={paidAmountLabelStyles}>
          Paid amount *
        </Typography>
        <TextField
          placeholder="0.00"
          value={paidAmount}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const validatedValue = handlePaidAmountChange(
              e.target.value,
              totalAmount
            );
            setPaidAmount(validatedValue);
          }}
          InputProps={{
            startAdornment: <InputAdornment position="start">£</InputAdornment>,
            inputProps: {min: 0, max: totalAmount, step: 0.01, type: 'number'},
          }}
          variant="outlined"
          sx={paidAmountInputStyles}
          onBlur={validatePaidAmount}
          error={!!paidAmountError}
          helperText={paidAmountError}
        />
      </Box>

      <Box sx={outstandingContainer}>
        <Typography
          variant="subtitle1"
          sx={{...outstandingAmountLabelStyles, fontWeight: 600}}
        >
          Outstanding amount
        </Typography>
        <Typography variant="h4" sx={outstandingAmountValueStyles}>
          £{outstandingAmount.toFixed(2)}
        </Typography>
      </Box>

      <Box sx={notificationSectionStyles}>
        <Box sx={emailNotificationContainerStyles}>
          <CheckBox
            label="Notify the patient about their outstanding balance via email"
            checked={notifyEmail}
            onChange={() => setNotifyEmail(!notifyEmail)}
            sx={emailCheckboxStyles}
          />

          <TextField
            placeholder="Email address"
            value={emailAddress}
            variant="outlined"
            InputProps={{
              readOnly: true,
            }}
            sx={notificationInputStyles}
          />
        </Box>

        <Box sx={smsNotificationContainerStyles}>
          <CheckBox
            label="Notify the patient about their outstanding balance via SMS"
            checked={notifySMS}
            onChange={() => setNotifySMS(!notifySMS)}
            sx={smsCheckboxStyles}
          />

          <TextField
            placeholder="Phone number"
            value={fullPhoneNumber}
            fullWidth
            variant="outlined"
            InputProps={{
              readOnly: true,
            }}
            sx={notificationInputStyles}
          />
        </Box>
      </Box>

      <Box sx={dialogButtonContainerStyles}>
        <FrontdeskButton
          variant="secondary"
          title="Cancel"
          onClick={handleDialogClose}
          className="cancel-red"
        />
        <FrontdeskButton
          variant="primary"
          title="Save & create payment"
          onClick={handleOutstandingSave}
          disabled={
            !paidAmount ||
            paidAmount === '0' ||
            paidAmount === '0.00' ||
            !!paidAmountError
          }
        />
      </Box>
    </Box>
  );

  const hasValidBillingItems =
    claim.items &&
    claim.items.length > 0 &&
    claim.items.some(item => item.id !== 0 && item.description);

  return (
    <Box sx={amountStatusContainerStyles}>
      <Box sx={amountStatusBoxStyles}>
        <Typography sx={amountLabelStyles}>
          {isOutstanding ? 'Outstanding amount' : SUMMARY_LABELS.AMOUNT}
        </Typography>

        <Box sx={amountDisplayContainerStyles}>
          <Typography sx={amountValueStyles}>
            {isOutstanding
              ? DEFAULT_VALUES.CURRENCY_SYMBOL +
                (
                  claim.outstanding_amount ??
                  totalAmount - (claim.approved_amount || 0)
                ).toLocaleString('en-GB', {minimumFractionDigits: 2})
              : totalAmount > 0
                ? DEFAULT_VALUES.CURRENCY_SYMBOL +
                  totalAmount.toLocaleString('en-GB', {
                    minimumFractionDigits: 2,
                  })
                : DEFAULT_VALUES.EMPTY_FIELD}
          </Typography>
          <StatusDropdown
            value={claim.status}
            options={availableStatusOptions}
            onChange={handleStatusChange}
            loading={loading}
            disabled={!hasValidBillingItems || isOutstandingStatus}
            disabledTooltip={
              !hasValidBillingItems
                ? TOOLTIP_MESSAGES.STATUS_DISABLED
                : isOutstandingStatus
                  ? TOOLTIP_MESSAGES.OUTSTANDING_STATUS_DISABLED
                  : undefined
            }
          />
        </Box>

        <DialogComponent
          open={isOutstandingDialogOpen}
          handleClose={handleDialogClose}
          dialogBody={dialogBody}
        />
      </Box>
      {isOutstanding && (
        <>
          <Divider orientation="vertical" flexItem sx={verticalDividerStyles} />
          <Box sx={amountStatusBoxWithLeftMarginStyles}>
            <Typography sx={amountLabelStyles}>
              {SUMMARY_LABELS.PAID_AMOUNT}
            </Typography>
            <Box sx={amountValueStyles}>
              {claim.approved_amount
                ? DEFAULT_VALUES.CURRENCY_SYMBOL +
                  claim.approved_amount.toLocaleString('en-GB', {
                    minimumFractionDigits: 2,
                  })
                : DEFAULT_VALUES.EMPTY_FIELD}
            </Box>
          </Box>
          {claim.payment_id && (
            <>
              <Divider
                orientation="vertical"
                flexItem
                sx={verticalDividerStyles}
              />
              <Box sx={amountStatusBoxWithLeftMarginStyles}>
                <Box sx={paymentLinkContainerStyles}>
                  <Typography sx={paymentLinkLabelStyles}>
                    {SUMMARY_LABELS.GO_TO_PAYMENT}
                  </Typography>
                  <Typography
                    sx={paymentLinkStyles}
                    onClick={handlePaymentLinkClick}
                  >
                    {SUMMARY_LABELS.OUTSTANDING_BALANCE}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
};

const BillingTable: React.FC<{claim: InsuranceClaim}> = ({claim}) => {
  const {roleName} = useAuthUser()!;
  const isFinanceRole = roleName === UserRoleName.Finance;
  const [isSearchPaymentItemOpen, setIsSearchPaymentItemOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const {sendDeleteBillingItemRequest, isDeleting} = useDeleteBillingItem();

  const canAddItem =
    isFinanceRole &&
    (claim.status === CLAIM_STATUSES.NEW_CLAIM ||
      claim.status === CLAIM_STATUSES.PENDING) &&
    claim.membership_no &&
    claim.insurer_name &&
    !claim.invoice_issued_at;

  const getAddItemDisabledReason = () => {
    if (!isFinanceRole) return VALIDATION_MESSAGES.ONLY_FINANCE_USERS;
    if (
      claim.status !== CLAIM_STATUSES.NEW_CLAIM &&
      claim.status !== CLAIM_STATUSES.PENDING
    ) {
      return VALIDATION_MESSAGES.ITEMS_NEW_PENDING_ONLY;
    }
    if (!claim.membership_no)
      return VALIDATION_MESSAGES.MEMBERSHIP_NUMBER_REQUIRED;
    if (!claim.insurer_name) return VALIDATION_MESSAGES.INSURER_NAME_REQUIRED;
    if (claim.invoice_issued_at)
      return VALIDATION_MESSAGES.INVOICE_ALREADY_GENERATED;
    return null;
  };

  const canDeleteItem =
    isFinanceRole &&
    (claim.status === CLAIM_STATUSES.NEW_CLAIM ||
      claim.status === CLAIM_STATUSES.PENDING);

  const items: BillingItem[] = claim.items || [
    DEFAULT_VALUES.DEFAULT_BILLING_ITEM,
  ];

  const handleDeleteItem = (itemId: number) => {
    if (!canDeleteItem) {
      return;
    }
    setItemToDelete(itemId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (itemToDelete) {
      sendDeleteBillingItemRequest(itemToDelete.toString());
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const columns: ICustomTableColumn<BillingItem>[] = [
    {
      key: 'ccsd_code',
      label: BILLING_TABLE_COLUMNS.CCSD_CODE,
      align: 'left',
      render: (
        value: string | number | null | undefined,
        rowData: BillingItem
      ) => {
        const displayValue = value || DEFAULT_VALUES.EMPTY_FIELD;
        return (
          <div className="billing-table-row-container">
            {canDeleteItem && (
              <div className="billing-table-trash-icon-container">
                <TrashIcon
                  width={20}
                  height={20}
                  className={`billing-table-trash-icon ${isDeleting ? 'deleting' : ''}`}
                  onClick={() => handleDeleteItem(rowData.id)}
                />
              </div>
            )}
            <span
              className={
                canDeleteItem
                  ? 'billing-table-ccsd-code-with-icon'
                  : 'billing-table-ccsd-code'
              }
            >
              {displayValue}
            </span>
          </div>
        );
      },
    },
    {
      key: 'description',
      label: BILLING_TABLE_COLUMNS.PROCEDURE_DESCRIPTION,
      align: 'left',
    },
    {
      key: 'category',
      label: BILLING_TABLE_COLUMNS.PROCEDURE_TYPE,
      align: 'left',
    },
    {
      key: 'amount',
      label: BILLING_TABLE_COLUMNS.PROCEDURE_PRICE,
      align: 'left',
    },
    {
      key: 'notes',
      label: BILLING_TABLE_COLUMNS.NOTES,
      align: 'left',
      render: value => value || DEFAULT_VALUES.EMPTY_FIELD,
    },
  ];

  return (
    <Box className="billing-table-container">
      <Typography sx={billingTitleStyles}>{SUMMARY_LABELS.BILLING}</Typography>
      <CustomTable
        columns={
          columns as unknown as ICustomTableColumn<Record<string, unknown>>[]
        }
        data={items as unknown as Record<string, unknown>[]}
        className="billing-table-header"
        showPagination={false}
      />
      {isFinanceRole && (
        <Tooltip
          title={getAddItemDisabledReason() || ''}
          placement="top"
          arrow
          PopperProps={{
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 2],
                },
              },
            ],
          }}
        >
          <div style={{display: 'inline-block'}}>
            <FrontdeskButton
              variant="primary"
              title={BUTTON_LABELS.ADD_ITEM}
              startIcon={<PlusSquareIcon width={20} height={20} />}
              className="w-fit font-normal text-[14px] mt-4"
              onClick={() => setIsSearchPaymentItemOpen(true)}
              disabled={!canAddItem}
            />
          </div>
        </Tooltip>
      )}
      <SearchPaymentItem
        isOpen={isSearchPaymentItemOpen}
        onClose={() => setIsSearchPaymentItemOpen(false)}
        onSelect={() => {
          setIsSearchPaymentItemOpen(false);
        }}
        insuranceClaimId={claim.id}
        insuranceClaim={claim}
      />
      <DeleteConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onDelete={handleConfirmDelete}
        isDisabled={isDeleting}
        title="billing item"
      />
    </Box>
  );
};

const ClaimDetailsComponent: React.FC = () => {
  const navigate = useNavigate();
  const {id} = useParams<{id: string}>();
  const {data: claim, isLoading, isError, error} = useClaimDetails(id);

  if (isLoading) return <Loading />;
  if (isError || !claim)
    return <div>{error?.message || 'Claim not found'}</div>;

  const handleBackClick = () => {
    navigate(
      getRoute(RouteSection.Finance, RoutePath.staff.finance.insuranceClaims)
    );
  };

  const handleViewFinancialRecord = () => {
    navigate(
      getRoute(RouteSection.Finance, RoutePath.staff.finance.financialRecord),
      {
        state: {patient_id: claim.patient_id, type: 'insurance-claim'},
      }
    );
  };

  return (
    <Box sx={containerStyles}>
      <Box sx={contentWrapperStyles}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBackClick}
          sx={backButtonStyles}
        >
          {BUTTON_LABELS.BACK}
        </Button>
        <Divider sx={verticalDividerStyles} />

        <div style={headerContainerStyles}>
          <Typography sx={headerTitleStyles}>
            {claim.invoice_number
              ? `Claim details / ${claim.invoice_number}`
              : 'Claim details'}
          </Typography>
          <FrontdeskButton
            variant="primary"
            title={BUTTON_LABELS.FINANCIAL_RECORD}
            onClick={handleViewFinancialRecord}
          />
        </div>
        <Divider sx={verticalDividerStyles} />

        <AmountStatusSection claim={claim} />
        <Divider sx={verticalDividerStyles} />
        <ClaimSummary claim={claim} />
        <Divider sx={verticalDividerStyles} />
        <BillingTable claim={claim} />
      </Box>
      <Box sx={bottomActionsContainerStyles}>
        <GenerateInvoiceButton insuranceClaimId={id} insuranceClaim={claim} />
      </Box>
    </Box>
  );
};

export default ClaimDetailsComponent;
